<script setup lang="ts">
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import TopBar from '@/common/components/TopBar.vue';
import SideNavigation from '@/common/components/SideNavigation.vue';
import LoggedOutDialog from '@/auth/components/LoggedOutDialog.vue';
import { useMqttClient } from '../api/mqttClient';
import { useInitializeMapStylesStore } from '@/mapStyles/api/initializeMapStyles';
import { useSelectedSiteStore } from '@/sites/stores/selectedSiteStore';
import { watch } from 'vue';
import { useSiteStore } from '@/sites/stores/siteStore';
import { useMapStyleStore, localStorageMapStyleKey } from 'shared/mapStyles/stores/mapStyleStore';
import { storeToRefs } from 'pinia';
import UnauthorizedDialog from '@/auth/components/UnauthorizedDialog.vue';
import { useRefreshState } from '@/state/composables/refreshState';
import { useServerSessionStore } from 'shared/serverSessions/stores/serverSessionStore';
import { useToast } from 'primevue';
import { useTranslations } from 'shared/translations/composables/useTranslations';

const allMapStylesKey = "allMapStyles";
useMqttClient();

const initializeMapStyles = useInitializeMapStylesStore();
const selectedSiteStore = useSelectedSiteStore();
const serverSessionStore = useServerSessionStore();

const siteStore = useSiteStore();
const translations = useTranslations();
const mapStyleStore = useMapStyleStore();
const { selectedStyleId } = storeToRefs(mapStyleStore);
const toast = useToast();

const refreshState = useRefreshState();

refreshState.initialize();
initializeMapStyles.execute();

watch(() => selectedSiteStore.siteId, async (newSite, oldSite) => {
  refreshState.refresh();
    // Save selected mapstyle to storage.
  let record: Record<number, string> = {}
  record = JSON.parse(localStorage.getItem(allMapStylesKey) ?? "{}");

  siteStore.sites.forEach(site => {
    if(site.id === oldSite) {
      const mapStyleId = localStorage.getItem(localStorageMapStyleKey);
      record[oldSite] = mapStyleId ?? "";
    } else {
      record[site.id] = record[site.id] ?? "";
    }
  });

  localStorage.setItem(allMapStylesKey, JSON.stringify(record))
  mapStyleStore.initialized = false;
  await initializeMapStyles.execute();

  if(selectedSiteStore.siteId) {
    const newStyleId = record[selectedSiteStore.siteId];
    localStorage.setItem(localStorageMapStyleKey, newStyleId ?? "");
    selectedStyleId.value = newStyleId
  }
});


watch(serverSessionStore, async () => {
  const { newSession, newVersion } = serverSessionStore;

  if (newSession) {
    await refreshState.refresh();
    serverSessionStore.newSession = false;
  }

  if (newVersion) {
    await refreshState.refresh();
    toast.add({
      summary: translations.value.generic.newVersion,
      severity: 'success',
    });
    serverSessionStore.newVersion = false;
  }
});

</script>

<template>
  <div class="main-layout">
    <TopBar>
      <slot name="header"></slot>
    </TopBar>
    <SideNavigation></SideNavigation>
    <main>
      <slot></slot>
      <ConfirmDialog></ConfirmDialog>
      <Toast position="top-center"></Toast>
    </main>
  </div>

  <LoggedOutDialog></LoggedOutDialog>
  <UnauthorizedDialog></UnauthorizedDialog>

</template>

<style scoped>

main {
  grid-row: 2;
  grid-column: 2;
  padding: 0.5rem;
  background-color: var(--p-surface-100);
  overflow-y: hidden;
}

.p-dark main {
  background-color: var(--p-surface-700);
}

</style>

<style>

.main-layout {
  height: 100%;
  overflow-y: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: auto 1fr;
}

.p-drawer-right {
  top: 50px!important;
  height: calc(100% - 50px)!important;
}

#app {
  display: grid;
  height: 100%;
  overflow-y: hidden;
  grid-template-columns: 1fr;
}

</style>
